import UTIF from "utif";

const MAX_EDGE = 2000;

export function isTiffBytes(bytes: Uint8Array): boolean {
  if (bytes.length < 4) {
    return false;
  }
  const littleEndian = bytes[0] === 0x49 && bytes[1] === 0x49 && bytes[2] === 0x2a && bytes[3] === 0x00;
  const bigEndian = bytes[0] === 0x4d && bytes[1] === 0x4d && bytes[2] === 0x00 && bytes[3] === 0x2a;
  return littleEndian || bigEndian;
}

function isJpeg(bytes: Uint8Array): boolean {
  return bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff;
}

function isPng(bytes: Uint8Array): boolean {
  return bytes.length >= 4 && bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47;
}

function fitRgba(rgba: Uint8Array, width: number, height: number): { rgba: Uint8Array; width: number; height: number } {
  const edge = Math.max(width, height);
  if (edge <= MAX_EDGE || width < 1 || height < 1) {
    return { rgba, width, height };
  }
  const scale = MAX_EDGE / edge;
  const nextWidth = Math.max(1, Math.round(width * scale));
  const nextHeight = Math.max(1, Math.round(height * scale));
  const fitted = new Uint8Array(nextWidth * nextHeight * 4);
  for (let y = 0; y < nextHeight; y += 1) {
    const sourceY = Math.min(height - 1, Math.floor(y / scale));
    for (let x = 0; x < nextWidth; x += 1) {
      const sourceX = Math.min(width - 1, Math.floor(x / scale));
      const from = (sourceY * width + sourceX) * 4;
      const to = (y * nextWidth + x) * 4;
      fitted[to] = rgba[from];
      fitted[to + 1] = rgba[from + 1];
      fitted[to + 2] = rgba[from + 2];
      fitted[to + 3] = rgba[from + 3];
    }
  }
  return { rgba: fitted, width: nextWidth, height: nextHeight };
}

function crc32(bytes: Uint8Array): number {
  let crc = 0xffffffff;
  for (let index = 0; index < bytes.length; index += 1) {
    crc ^= bytes[index];
    for (let bit = 0; bit < 8; bit += 1) {
      const mask = -(crc & 1);
      crc = (crc >>> 1) ^ (0xedb88320 & mask);
    }
  }
  return (crc ^ 0xffffffff) >>> 0;
}

function pngChunk(type: string, data: Uint8Array): Uint8Array {
  const typeBytes = new TextEncoder().encode(type);
  const chunk = new Uint8Array(12 + data.length);
  const view = new DataView(chunk.buffer);
  view.setUint32(0, data.length);
  chunk.set(typeBytes, 4);
  chunk.set(data, 8);
  const crcInput = chunk.subarray(4, 8 + data.length);
  view.setUint32(8 + data.length, crc32(crcInput));
  return chunk;
}

async function rgbaToPng(rgba: Uint8Array, width: number, height: number): Promise<Uint8Array> {
  const raw = new Uint8Array(height * (1 + width * 4));
  for (let y = 0; y < height; y += 1) {
    const row = y * (1 + width * 4);
    raw.set(rgba.subarray(y * width * 4, (y + 1) * width * 4), row + 1);
  }
  const compressed = new Uint8Array(await new Response(new Blob([raw]).stream().pipeThrough(new CompressionStream("deflate"))).arrayBuffer());
  const header = new Uint8Array(13);
  const view = new DataView(header.buffer);
  view.setUint32(0, width);
  view.setUint32(4, height);
  header[8] = 8;
  header[9] = 6;
  const parts = [
    Uint8Array.from([137, 80, 78, 71, 13, 10, 26, 10]),
    pngChunk("IHDR", header),
    pngChunk("IDAT", compressed),
    pngChunk("IEND", new Uint8Array()),
  ];
  const png = new Uint8Array(parts.reduce((total, part) => total + part.length, 0));
  let offset = 0;
  for (const part of parts) {
    png.set(part, offset);
    offset += part.length;
  }
  return png;
}

/** Scanned Tingbog rids are often TIFF, which the browser cannot draw. */
export async function tiffToPngs(bytes: Uint8Array): Promise<Uint8Array[]> {
  const buffer = new Uint8Array(bytes);
  const pages = UTIF.decode(buffer);
  const pngs: Uint8Array[] = [];
  for (const page of pages) {
    try {
      UTIF.decodeImage(buffer, page);
      const rgba = UTIF.toRGBA8(page);
      if (!page.width || !page.height || !rgba?.length) {
        continue;
      }
      const fitted = fitRgba(rgba, page.width, page.height);
      pngs.push(await rgbaToPng(fitted.rgba, fitted.width, fitted.height));
    } catch (error) {
      console.warn("[bilag] TIFF page failed", error);
    }
  }
  return pngs;
}

async function browserImageToPng(blob: Blob): Promise<Uint8Array | null> {
  if (typeof createImageBitmap !== "function" || typeof document === "undefined") {
    return null;
  }
  try {
    const bitmap = await createImageBitmap(blob);
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    canvas.getContext("2d")?.drawImage(bitmap, 0, 0);
    bitmap.close?.();
    const png = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/png"));
    return png ? new Uint8Array(await png.arrayBuffer()) : null;
  } catch {
    return null;
  }
}

/** PNG or JPEG bytes that can be placed on a figure page. TIFF is converted. */
export async function bilagFigurePngs(blob: Blob): Promise<Uint8Array[] | null> {
  if (!blob || blob.size < 8) {
    return null;
  }
  const bytes = new Uint8Array(await blob.arrayBuffer());
  if (isJpeg(bytes) || isPng(bytes)) {
    return [bytes];
  }
  if (isTiffBytes(bytes)) {
    try {
      const pngs = await tiffToPngs(bytes);
      if (pngs.length) {
        return pngs;
      }
    } catch (error) {
      console.warn("[bilag] TIFF decode failed", error);
    }
  }
  const image = await browserImageToPng(blob);
  return image ? [image] : null;
}
